import { useState } from "react";
import Header from "../../commonComponents/header/Header";
import arrowImg from "../../../assets/images/arrow-white-icon.svg";
import moonshotInfoImg from "../../../assets/images/moonshot-inro-right.svg";
import { useNavigate } from "react-router-dom";
import moonshotLogo from "../../../assets/images/moonshot-logo.svg";

const needleEyeSections = [
  {
    type: "title",
    text: "NeedleEye for Mobile - Real-time Visual Integrity for AI Interviews",
  },
  {
    type: "para",
    text: "NeedleEye Mobile is a companion mobile application designed to reinforce the integrity of AI-driven interviews and assessments conducted on the Needles & Haystacks platform. It acts as an independent, secondary observation layer, providing visual verification of the candidate's interview/assessment environment without disrupting the natural flow of the session itself.",
  },
  {
    type: "para",
    text: "Once installed on an Android or iOS device and securely paired with an active session, NeedleEye Mobile operates in the background, intelligently prompting the candidate at randomized intervals to capture images of their screen and/or immediate surroundings. These prompts are intentionally non-linear and unpredictable, ensuring that candidates cannot anticipate or manipulate the capture process.",
  },
  { type: "heading", text: "How It Works" },
  {
    type: "para",
    text: "At the start of an interview/assessment, the candidate is guided through a quick pairing process that links their mobile device to the active session. Once connected, the app remains synchronized throughout the session duration.",
  },
  { type: "subheading", text: "Start of the interview / assessment" },
  {
    type: "bullets",
    items: [
      "The app prompts the user to take a 360-degree panoramic video of their surroundings.",
      "Once complete, the video is securely uploaded in real time to the platform for post-session analysis.",
    ],
  },
  { type: "subheading", text: "During the interview / assessment" },
  {
    type: "bullets",
    items: [
      "The app triggers randomized prompts at varying intervals.",
      "Each prompt requires the candidate to capture a photo of their screen and/or surrounding workspace.",
      "Images are securely uploaded in real time to the platform for post-session analysis.",
      "The process is designed to be minimally intrusive, typically requiring only a few seconds per prompt.",
    ],
  },
  {
    type: "para",
    text: "The randomness of prompts follows a non-deterministic pattern, ensuring coverage across the entire duration of the session rather than clustering at predictable points.",
  },
  { type: "heading", text: "What It Detects" },
  {
    type: "para",
    text: "NeedleEye Mobile provides visual evidence to identify potential malpractice indicators, including:",
  },
  {
    type: "bullets",
    items: [
      "Use of secondary devices (phones, tablets, hidden screens)",
      "Presence of unauthorized assistance (another person in proximity)",
      "Reference to external materials (notes, books, printed content)",
      "Screen anomalies indicating mirroring or hidden displays",
    ],
  },
  {
    type: "para",
    text: "By capturing real-world context beyond the primary interview device, the app significantly reduces blind spots in remote proctoring.",
  },
  { type: "heading", text: "Privacy & Candidate Experience" },
  {
    type: "para",
    text: "NeedleEye Mobile is built with a strong emphasis on privacy and transparency:",
  },
  {
    type: "bullets",
    items: [
      "Captures are only triggered during active interview sessions",
      "Data is encrypted in transit and at rest",
      "Candidates are informed upfront about the process and purpose",
    ],
  },
  {
    type: "para",
    text: "The goal is not surveillance, but verification with consent—ensuring fairness for all participants.",
    italic: true,
  },
  { type: "heading", text: "Why It Matters" },
  {
    type: "para",
    text: "In remote AI interviews, traditional proctoring methods often fail to account for off-screen activity. NeedleEye Mobile bridges this gap by:",
  },
  {
    type: "bullets",
    items: [
      "Providing contextual, time-stamped visual evidence",
      "Acting as a deterrent against malpractice",
      "Enhancing the credibility of interview outcomes",
    ],
  },
  {
    type: "para",
    text: "For employers, it offers confidence. For genuine candidates, it ensures a level playing field.",
  },
];

const MoonshotsIntroduction = () => {
  const navigate = useNavigate();
  const [showPanel, setShowPanel] = useState(false);

  const handleProceed = () => {
    navigate("/needleeye-setup");
  };

  const handleLinkClick = () => {
    setShowPanel(true);
  };

  const handleClosePanel = () => {
    setShowPanel(false);
  };

  return (
    <div className="flex flex-col min-h-screen bg-white overflow-x-hidden px-4 pt-4">
      {/* Header */}
      <Header />

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col md:flex-row items-center justify-center px-1 pt-5 max-w-9xl mx-auto">
        {/* Left Section */}
        <div className="w-full md:w-1/2 flex justify-center items-center px-4 md:px-6 lg:px-8">
          <img
            src={moonshotInfoImg}
            alt="moonshotInfoImg"
            className="object-contain w-full max-w-[520px] h-auto rounded-3xl"
          />
        </div>

        {/* Divider */}
        {!showPanel && (
          <div className="hidden md:block self-stretch w-[1px] bg-dividerColor mx-4 my-[5%]" />
        )}

        {/* Right Section */}
        {showPanel ? (
          <div
            className="w-full md:w-1/2 flex flex-col mt-6 md:mt-0"
            style={{
              boxShadow:
                "0 4px 24px 0 rgba(0, 0, 0, 0.10), 0 1.5px 6px 0 rgba(0, 0, 0, 0.06)",
              borderRadius: "12px",
              border: "1px solid #F3F4F6",
              background: "#ffffff",
              padding: "16px",
            }}
          >
            {/* Panel Header */}
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                paddingBottom: "12px",
                marginBottom: "12px",
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              {/* Left: logo icon + NeedleEye label + subtitle stacked */}
              <div
                style={{ display: "flex", flexDirection: "column", gap: "2px" }}
              >
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <img
                    src={moonshotLogo}
                    alt="moonshot logo"
                    style={{ height: "50px", width: "auto" }}
                  />
                </div>
                <p
                  style={{
                    fontSize: "13px",
                    color: "#6B7280",
                    margin: 0,
                    paddingLeft: "2px",
                  }}
                >
                  NeedleEye Mobile - Real-time Visual Integrity for AI
                  Interviews
                </p>
              </div>

              {/* Right: Close button */}
              <button
                onClick={handleClosePanel}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  backgroundColor: "#002147",
                  color: "#ffffff",
                  fontSize: "15px",
                  padding: "6px 14px",
                  borderRadius: "6px",
                  border: "none",
                  cursor: "pointer",
                  fontWeight: 500,
                  whiteSpace: "nowrap",
                  marginTop: "2px",
                }}
              >
                &#x2715; Close
              </button>
            </div>

            {/* Scrollable Content */}
            <div
              style={{
                overflowY: "auto",
                maxHeight: "60vh",
                paddingRight: "8px",
              }}
            >
              {needleEyeSections.map((section, idx) => {
                if (section.type === "title") {
                  return null; // shown as subtitle in header
                }
                if (section.type === "heading") {
                  return (
                    <p
                      key={idx}
                      style={{
                        fontSize: "16px",
                        fontWeight: 500,
                        color: "#111827",
                        marginTop: "32px",
                        marginBottom: "12px",
                        lineHeight: "1.6",
                      }}
                    >
                      {section.text}
                    </p>
                  );
                }
                if (section.type === "subheading") {
                  return (
                    <p
                      key={idx}
                      style={{
                        fontSize: "16px",
                        fontWeight: 500,
                        color: "#111827",
                        marginTop: "24px",
                        marginBottom: "8px",
                        lineHeight: "1.6",
                      }}
                    >
                      {section.text}
                    </p>
                  );
                }
                if (section.type === "para") {
                  return (
                    <p
                      key={idx}
                      style={{
                        fontSize: "16px",
                        fontWeight: section.italic ? 600 : 400,
                        fontStyle: section.italic ? "italic" : "normal",
                        color: "#374151",
                        marginBottom: "16px",
                        lineHeight: "1.7",
                      }}
                    >
                      {section.text}
                    </p>
                  );
                }
                if (section.type === "bullets") {
                  return (
                    <ul
                      key={idx}
                      style={{
                        paddingLeft: "20px",
                        marginBottom: "16px",
                        listStyleType: "disc",
                      }}
                    >
                      {section.items.map((item, i) => (
                        <li
                          key={i}
                          style={{
                            fontSize: "16px",
                            fontWeight: 400,
                            color: "#374151",
                            lineHeight: "1.7",
                            marginBottom: "4px",
                          }}
                        >
                          {item}
                        </li>
                      ))}
                    </ul>
                  );
                }
                return null;
              })}
            </div>
            {/* Proceed button aligned to right */}
            <div className="flex justify-end pt-4">
              <button
                onClick={handleProceed}
                className="btn-size flex bg-btnBgColor items-center px-6 py-1.5 text-white text-[16px]"
              >
                Proceed
                <img src={arrowImg} alt="arrow" className="ml-2" />
              </button>
            </div>
          </div>
        ) : (
          <div className="w-full md:w-1/2 flex flex-col justify-center px-4 sm:px-6 md:px-8 lg:px-10 mt-6 md:mt-0">
            <div className="max-w-[720px]">
              {/* Centered Logo */}
              <div className="w-full flex justify-center mb-6">
                <img
                  src={moonshotLogo}
                  alt="moonshot logo"
                  className="h-14 sm:h-16 md:h-20 lg:h-24"
                />
              </div>

              {/* Body */}
              <p
                className="mb-4 leading-relaxed outer-font-color
        text-[14px] sm:text-[16px] md:text-[18px] lg:text-[24px]"
              >
                <span className="font-medium">NeedleEye (Mobile)</span> is the
                companion app for{" "}
                <span className="font-medium">NeedleInTheHaystack</span> and is
                required to be installed on your phone.
              </p>

              <p
                className="mb-4 leading-relaxed outer-font-color
        text-[14px] sm:text-[16px] md:text-[18px] lg:text-[24px]"
              >
                To know more about NeedleEye, &nbsp;
                <a
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    handleLinkClick();
                  }}
                  className="text-blue-600 underline"
                >
                  click here
                </a>
                .
              </p>

              <p
                className="leading-relaxed outer-font-color
        text-[14px] sm:text-[16px] md:text-[18px] lg:text-[24px]"
              >
                Please click on <span className="font-medium">"Proceed"</span>{" "}
                for instructions to install and run NeedleEye.
              </p>

              {/* Proceed button aligned to right */}
              <div className="flex justify-end pt-6">
                <button
                  onClick={handleProceed}
                  className="btn-size flex bg-btnBgColor items-center px-6 py-1.5 text-white text-[16px]"
                >
                  Proceed
                  <img src={arrowImg} alt="arrow" className="ml-2" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="w-full py-4 text-[10px] text-gray-600 text-center px-4">
  © {new Date().getFullYear() > 2026 ? `2026-${new Date().getFullYear()}` : '2026'}, Needles and Haystacks Technology Pvt Ltd.
      </div>
    </div>
  );
};

export default MoonshotsIntroduction;
